import { useState, type DragEvent } from 'react';
import {
  Ban,
  GitBranch,
  GripVertical,
  MoreVertical,
  Sparkles,
  Target,
  User,
  UserPlus,
} from 'lucide-react';
import { Badge, cn } from '../../ui';
import { BOARD_STATUSES } from '../../features/tasks/taskModel';
import type { BoardStatus, Task } from '../../features/tasks/types';
import type { SubtaskProgress } from '../../features/tasks/taskModel';
import type { DirectoryPerson } from '../../features/teams/types';
import { PeopleInput } from '../common/PeopleInput';

export interface SprintBoardCardProps {
  task: Task;
  /** Resolved goal title (BHAG name) when the task links a goal. */
  goalName?: string;
  /** Subtask roll-up (done/total). The badge renders only when total > 0. */
  subtaskProgress?: SubtaskProgress;
  dragging?: boolean;
  /**
   * Drag handlers. When omitted (e.g. the By-Person view) the card is not
   * draggable and renders without the grab handle — a compact, click-to-open
   * variant that stays visually consistent with the board.
   */
  onDragStart?: (e: DragEvent<HTMLElement>, task: Task) => void;
  onDragEnd?: () => void;
  /** Opens the task's detail page (title click). */
  onOpen: (task: Task) => void;
  // ---- Inline (drag-free) actions. All optional → when omitted the control ----
  // ---- simply isn't rendered, so the card degrades to display-only. ----------
  /** Sets the task's board lane inline (page routes DONE to the completion prompt). */
  onSetStatus?: (task: Task, boardStatus: BoardStatus) => void;
  /** Toggles the task's blocked state. */
  onToggleBlocked?: (task: Task) => void;
  /** Removes the task from the sprint (returns it to the backlog). */
  onRemoveFromSprint?: (task: Task) => void;
  /** Toggles the task's unplanned/ad-hoc flag. */
  onToggleAdhoc?: (task: Task) => void;
  /**
   * Assigns (or clears, with an empty string) the task's owner from the card
   * menu — the sprint-first "assign later" path. When omitted the assign
   * control isn't rendered.
   */
  onAssignOwner?: (task: Task, owner: string) => void;
  /** Directory people to suggest in the assign-owner control. */
  people?: DirectoryPerson[];
  /** Disables the inline controls while a board write is in flight. */
  busy?: boolean;
}

/** Short lane labels for the compact inline status control. */
const LANE_SHORT: Record<BoardStatus, string> = {
  TODO: 'To Do',
  IN_PROGRESS: 'In Prog',
  DONE: 'Done',
};

/**
 * Compact, drag-free lane control: three segments (To Do / In Prog / Done) that
 * set {@link Task.boardStatus} directly, highlighting the current lane. Works in
 * both the Status board and the By-Person view.
 */
function StatusSegments({
  task,
  onSet,
  busy,
}: {
  task: Task;
  onSet: (task: Task, boardStatus: BoardStatus) => void;
  busy?: boolean;
}) {
  const current = (task.boardStatus ?? 'TODO') as BoardStatus;
  return (
    <div
      role="group"
      aria-label="Set status"
      className="inline-flex rounded-lg border border-surface-border bg-surface-2 p-0.5"
    >
      {BOARD_STATUSES.map((lane) => {
        const active = current === lane.value;
        return (
          <button
            key={lane.value}
            type="button"
            disabled={busy}
            aria-pressed={active}
            title={`Set to ${lane.label}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!active) onSet(task, lane.value);
            }}
            className={cn(
              'rounded-md px-1.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50',
              active
                ? 'bg-surface-3 text-zinc-100'
                : 'text-zinc-500 hover:text-zinc-300',
            )}
          >
            {LANE_SHORT[lane.value]}
          </button>
        );
      })}
    </div>
  );
}

/** A small per-card menu: assign owner, remove from sprint, and toggle ad-hoc. */
function CardMenu({
  task,
  onRemoveFromSprint,
  onToggleAdhoc,
  onAssignOwner,
  people,
  busy,
}: {
  task: Task;
  onRemoveFromSprint?: (task: Task) => void;
  onToggleAdhoc?: (task: Task) => void;
  onAssignOwner?: (task: Task, owner: string) => void;
  people?: DirectoryPerson[];
  busy?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [alias, setAlias] = useState(task.owner ?? '');
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        disabled={busy}
        aria-label="Task actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setAlias(task.owner ?? '');
          setOpen((o) => !o);
        }}
        className="rounded-md p-1 text-zinc-500 transition-colors hover:bg-surface-3 hover:text-zinc-300 disabled:opacity-50"
      >
        <MoreVertical className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <>
          {/* Outside-click catcher. */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            className="fixed inset-0 z-10 cursor-default"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div
            role="menu"
            className="absolute right-0 z-20 mt-1 w-60 overflow-hidden rounded-xl border border-surface-border bg-surface-2 py-1 shadow-pop"
          >
            {onAssignOwner && (
              <div
                className="border-b border-surface-border px-3 py-2"
                onClick={(e) => e.stopPropagation()}
              >
                <PeopleInput
                  label={task.owner ? 'Reassign owner' : 'Assign owner'}
                  value={alias}
                  people={people ?? []}
                  placeholder="alias…"
                  onChange={setAlias}
                />
                <div className="mt-2 flex items-center gap-2">
                  <button
                    role="menuitem"
                    type="button"
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpen(false);
                      onAssignOwner(task, alias.trim());
                    }}
                    className="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-accent/90 disabled:opacity-50"
                  >
                    <UserPlus className="h-3 w-3" aria-hidden="true" />
                    Assign
                  </button>
                  {task.owner && (
                    <button
                      role="menuitem"
                      type="button"
                      disabled={busy}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpen(false);
                        onAssignOwner(task, '');
                      }}
                      className="rounded-md border border-surface-border px-2 py-1 text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-200 disabled:opacity-50"
                    >
                      Unassign
                    </button>
                  )}
                </div>
              </div>
            )}
            {onToggleAdhoc && (
              <button
                role="menuitem"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onToggleAdhoc(task);
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-zinc-300 transition-colors hover:bg-surface-3"
              >
                <Sparkles className="h-3.5 w-3.5 text-amber-300" aria-hidden="true" />
                {task.adhoc ? 'Mark as planned' : 'Mark unplanned (ad-hoc)'}
              </button>
            )}
            {onRemoveFromSprint && (
              <button
                role="menuitem"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onRemoveFromSprint(task);
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-zinc-300 transition-colors hover:bg-surface-3"
              >
                <User className="h-3.5 w-3.5 text-zinc-500" aria-hidden="true" />
                Remove from sprint
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A Sprint Board task card. Deliberately minimal (§14): Title, Owner, the
 * Effort(d) • Story Points • Confidence% metric line, the linked Goal (BHAG)
 * name when present, a subtask progress badge (n/m) when it has subtasks, and a
 * ⚠ Blocked / ⚡ Ad-hoc badge overlay. When inline-action callbacks are supplied
 * it also renders a compact, drag-free status control, a Blocked toggle, and a
 * card menu (remove / mark unplanned) — usable in BOTH the Status and By-Person
 * views. Draggable on the Status board; a static click-to-open card otherwise.
 */
export function SprintBoardCard({
  task,
  goalName,
  subtaskProgress,
  dragging,
  onDragStart,
  onDragEnd,
  onOpen,
  onSetStatus,
  onToggleBlocked,
  onRemoveFromSprint,
  onToggleAdhoc,
  onAssignOwner,
  people,
  busy,
}: SprintBoardCardProps) {
  const draggable = !!onDragStart;
  const hasMenu = !!onRemoveFromSprint || !!onToggleAdhoc || !!onAssignOwner;
  const hasControls = !!onSetStatus || !!onToggleBlocked;
  const metricParts: string[] = [];
  if (task.type === 'Spike') {
    metricParts.push('Spike');
  } else if (typeof task.effortDays === 'number' && task.effortDays > 0) {
    metricParts.push(`${task.effortDays}d`);
  }
  if (typeof task.storyPoints === 'number') {
    metricParts.push(`${task.storyPoints} SP`);
  }
  if (typeof task.confidence === 'number') {
    metricParts.push(`${task.confidence}%`);
  }

  return (
    <div
      draggable={draggable}
      onDragStart={draggable ? (e) => onDragStart?.(e, task) : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      className={[
        'group rounded-xl border border-surface-border bg-surface-1 p-3 shadow-sm',
        'transition-colors hover:border-zinc-600',
        dragging ? 'opacity-40' : 'opacity-100',
      ].join(' ')}
    >
      <div className="flex items-start gap-2">
        {draggable && (
          <GripVertical
            className="mt-0.5 h-4 w-4 shrink-0 cursor-grab text-zinc-600"
            aria-hidden="true"
          />
        )}
        <button
          type="button"
          onClick={() => onOpen(task)}
          className="flex-1 text-left text-sm font-medium text-zinc-100 hover:text-white"
        >
          {task.title}
        </button>
        {hasMenu && (
          <CardMenu
            task={task}
            onRemoveFromSprint={onRemoveFromSprint}
            onToggleAdhoc={onToggleAdhoc}
            onAssignOwner={onAssignOwner}
            people={people}
            busy={busy}
          />
        )}
      </div>

      <div
        className={[
          'mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5',
          draggable ? 'pl-6' : '',
        ].join(' ')}
      >
        {task.blocked && (
          <Badge tone="danger" dot>
            ⚠ Blocked
          </Badge>
        )}
        {task.adhoc && (
          <Badge tone="warning" dot>
            ⚡ Ad-hoc
          </Badge>
        )}
        {subtaskProgress && subtaskProgress.total > 0 && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-surface-3 px-1.5 py-0.5 text-xs tabular-nums text-zinc-400"
            title={`${subtaskProgress.done} of ${subtaskProgress.total} subtasks done`}
          >
            <GitBranch className="h-3 w-3" aria-hidden="true" />
            {subtaskProgress.done}/{subtaskProgress.total}
          </span>
        )}
        {metricParts.length > 0 && (
          <span className="text-xs tabular-nums text-zinc-400">
            {metricParts.join(' · ')}
          </span>
        )}
      </div>

      {(goalName || task.owner || (hasMenu && !!onAssignOwner)) && (
        <div
          className={[
            'mt-2 flex flex-wrap items-center gap-x-3 gap-y-1',
            draggable ? 'pl-6' : '',
          ].join(' ')}
        >
          {goalName && (
            <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
              <Target className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{goalName}</span>
            </span>
          )}
          {task.owner ? (
            <span className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-500">
              <User className="h-3 w-3" aria-hidden="true" />
              {task.owner}
            </span>
          ) : (
            onAssignOwner && (
              <span
                className="ml-auto inline-flex items-center gap-1 text-xs italic text-zinc-600"
                title="No owner yet — assign from the ⋮ menu"
              >
                <UserPlus className="h-3 w-3" aria-hidden="true" />
                Unassigned
              </span>
            )
          )}
        </div>
      )}

      {/* Inline (drag-free) controls: status segments + Blocked toggle. */}
      {hasControls && (
        <div
          className={[
            'mt-2.5 flex flex-wrap items-center gap-2',
            draggable ? 'pl-6' : '',
          ].join(' ')}
        >
          {onSetStatus && (
            <StatusSegments task={task} onSet={onSetStatus} busy={busy} />
          )}
          {onToggleBlocked && (
            <button
              type="button"
              disabled={busy}
              aria-pressed={!!task.blocked}
              title={task.blocked ? 'Unblock' : 'Mark blocked'}
              onClick={(e) => {
                e.stopPropagation();
                onToggleBlocked(task);
              }}
              className={cn(
                'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50',
                task.blocked
                  ? 'border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20'
                  : 'border-surface-border text-zinc-500 hover:text-zinc-300',
              )}
            >
              <Ban className="h-3 w-3" aria-hidden="true" />
              {task.blocked ? 'Blocked' : 'Block'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default SprintBoardCard;
